import {
  Box, Slider, Tooltip, Typography, withStyles,
} from '@material-ui/core';
import format from 'date-fns/format';
import React, { useCallback, useMemo, useState } from 'react';
import HeaderPopover from 'components/layout/HeaderPopover';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import FullscreenIcon from '@material-ui/icons/Fullscreen';
import SkipPreviousIcon from '@material-ui/icons/SkipPrevious';
import SkipNextIcon from '@material-ui/icons/SkipNext';
import StopIcon from '@material-ui/icons/Pause';

import X05 from '@images/icons/svg/X0.5.svg';
import X1 from '@images/icons/svg/X1.svg';
import X25 from '@images/icons/svg/X2.5.svg';
import X5 from '@images/icons/svg/X5.svg';
import { MovieFilter } from '@material-ui/icons';
import { withMe } from 'components/MeUserWrapper';
import { useTranslation } from 'react-i18next';
import {
  MarkDrill,
  PlayerControlsProps,
  PlayerDataInterval,
  PlayerDataIntervals,
  SliderDrillsProps,
} from '../../types/webPlayer';
import BaseButton from '../form/BaseButton';

const localTimezoneOffset = -new Date().getTimezoneOffset() * 60000;

const CustomTooltip = withStyles(() => ({
  tooltip: {
    top: '10px',
  },
}))(Tooltip);

const SliderDrills = (props: SliderDrillsProps) => {
  const {
    min, max, marks, currentTime,
  } = props;

  const isDrillSelected = (startTime, endTime) => currentTime >= startTime && currentTime <= endTime;

  const marksBars = useMemo(() => {
    const out: [MarkDrill, MarkDrill][] = [];
    for (let i = 0; i < marks.length; i += 2) {
      out.push([marks[i], marks[i + 1]]);
    }

    return out;
  }, [marks]);

  if (min && max && marks && marks.length) {
    const range = max - min;

    return (
      <Box className="player-marks-overlay">
        {
          marksBars.map((bar) => (
            <Box
              key={bar[0].index}
              position="absolute"
              left={`${(bar[0].value - min) / range * 100}%`}
              width={`${(bar[1].value - bar[0].value) / range * 100}%`}
            >
              <CustomTooltip
                title={`${bar[0].label} - ${bar[1].label}`}
                arrow
                placement="top"
              >
                <span style={{
                  cursor: 'pointer',
                  textAlign: 'center',
                  fontWeight: isDrillSelected(bar[0].value, bar[1].value) ? 800 : 400,
                }}
                >
                  {`D${bar[0].index}`}
                </span>
              </CustomTooltip>
            </Box>
          ))
        }
      </Box>
    );
  }

  return null;
};

const SliderDataStatus = (props: {
  loadingInterval: PlayerDataInterval,
  min: number,
  max: number,
  marks: PlayerDataIntervals,
}) => {
  const {
    loadingInterval, min, max, marks,
  } = props;

  if (min && max && marks && marks.length) {
    const range = max - min;

    return (
      <div className="player-data-wrapper">
        <Box className="player-data-loading">
          {
            loadingInterval
            && (
            <Box
              key="loading-data"
              position="absolute"
              left={`${(loadingInterval[0] - min) / range * 100}%`}
              width={`${(loadingInterval[1] - loadingInterval[0]) / range * 100}%`}
            />
            )
          }
        </Box>
        <Box className="player-data-overlay">
          {
            marks.map((bar) => (
              <Box
                key={`${bar[0]}${bar[1]}`}
                position="absolute"
                left={`${(bar[0] - min) / range * 100}%`}
                width={`${(bar[1] - bar[0]) / range * 100}%`}
              />
            ))
          }
        </Box>
      </div>
    );
  }

  return null;
};

const PlayerControls = (props: PlayerControlsProps) => {
  const {
    currDrillRef,
    currentDrill,
    currentTime,
    drillTimes,
    enabled,
    endTimestamp,
    goToNextDrill,
    goToPrevDrill,
    handleChange,
    handleFullscreenClick,
    handlePlayClick,
    handleRecordTrack,
    intervals,
    isRecording,
    loadingInterval,
    marks,
    me,
    playerSpeed,
    playerStatus,
    setPlayerSpeed,
    startTimestamp,
    timeFormat,
    timestamps,
    valueLabelFormat,
  } = props;

  const { t } = useTranslation();

  const timezoneOffset = useMemo(() => (startTimestamp
    ? -new Date(startTimestamp).getTimezoneOffset() * 60000
    : 0), [startTimestamp]);

  const startDrill = drillTimes && drillTimes.length > 0 && currentDrill !== null && drillTimes[currentDrill].start;
  const endDrill = drillTimes && drillTimes.length > 0 && currentDrill !== null && drillTimes[currentDrill].end;

  const startTime = useMemo(() => (startDrill || (startTimestamp
    ? new Date(startTimestamp).getTime() + timezoneOffset
    : 0)), [startDrill, startTimestamp, timezoneOffset]);

  const endTime = useMemo(() => (endDrill || (endTimestamp
    ? new Date(endTimestamp).getTime() + timezoneOffset
    : 0)), [endDrill, endTimestamp, timezoneOffset]);

  const [hoverTime, setHoverTime] = useState<number | undefined>(currentTime);
  const [showRelativeTime, setShowRelativeTime] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ left: 0, top: 0 });

  const activeDrill = useMemo(() => {
    if (!currentTime || !drillTimes?.length) {
      return null;
    }

    return drillTimes.findIndex((drill) => drill.start <= currentTime && drill.end >= currentTime);
  }, [currentTime, drillTimes]);

  const getRealTime = useCallback((time: number) => {
    if (time !== undefined && time >= 0) {
      return format(
        time - (showRelativeTime ? startTime : 0) - (showRelativeTime ? localTimezoneOffset : timezoneOffset),
        timeFormat,
      );
    }

    return '';
  }, [showRelativeTime, startTime, localTimezoneOffset, timezoneOffset, timeFormat]);

  // get currentTime based on mouse position on slider:
  const getCurrentTimeValue = useCallback((event) => {
    const slider = event.currentTarget;
    const rect = slider.getBoundingClientRect();
    const xPos = event.clientX - rect.left;
    return Math.round((xPos / rect.width) * (endTime - startTime) + startTime);
  }, [endTime, startTime]);

  const updateCurrentDrill = useCallback((currTime) => {
    if (currTime === 0) return;

    drillTimes.forEach(({ start }, index) => {
      if (currTime >= start && currTime <= drillTimes[index + 1].end) {
        currDrillRef.current = index + 1;
      }
    });
  }, [drillTimes, currDrillRef.current]);

  const handleSliderChange = useCallback((event, newValue, isDrag = false) => {
    setHoverTime(undefined);
    if (enabled) {
      handleChange(event, newValue, false, isDrag);
      if (Object.hasOwn(event, 'getBoundingClientRect')) {
        const currTime = getCurrentTimeValue(event);
        updateCurrentDrill(currTime);
      }
    }
  }, [handleChange]);

  const handleMouseMove = useCallback((event) => {
    const slider = event.currentTarget;
    const rect = slider.getBoundingClientRect();
    const xPos = event.clientX - rect.left;
    const currTime = Math.round((xPos / rect.width) * (endTime - startTime) + startTime);
    setHoverTime(currTime);
    setTooltipPosition({
      left: xPos,
      top: 18,
    });
  }, [endTime, startTime]);

  const handleMouseLeave = useCallback(() => setHoverTime(undefined), []);
  const handlePlay = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.currentTarget.blur();
    handlePlayClick();
  }, [handlePlayClick]);

  return (
    <Box className="webplayer-controls" style={{ position: 'relative' }}>

      <Box display="flex" alignItems="center" mt={2}>
        <Box width="80px" className="player-time-label" alignItems="center" display="flex">
          <Typography
            gutterBottom
            title={`switch to ${showRelativeTime ? 'H:m:s' : 'relative time'}`}
            onClick={() => setShowRelativeTime((prev) => !prev)}
            style={{
              cursor: 'pointer',
              margin: '0',
            }}
          >
            {startTimestamp ? getRealTime(startTime) : '--:--:--'}
          </Typography>
        </Box>

        <Box
          width="100%"
          display="flex"
          className="player-time-label player-time-current"
          alignItems="center"
          textAlign="center"
        >
          <Typography
            gutterBottom
            title={`switch to ${showRelativeTime ? 'H:m:s' : 'relative time'}`}
            onClick={() => setShowRelativeTime((prev) => !prev)}
            style={{
              cursor: 'pointer',
              margin: '0',
              fontWeight: 'bold',
              fontSize: '1.25rem',
            }}
          >
            {currentTime ? getRealTime(currentTime) : '--:--:--'}
          </Typography>
        </Box>

        <Box width="80px" ml="auto" alignItems="center" className="player-time-label">
          <Typography
            title={`switch to ${showRelativeTime ? 'H:m:s' : 'relative time'}`}
            gutterBottom
            onClick={() => setShowRelativeTime((prev) => !prev)}
            style={{
              cursor: 'pointer',
              margin: '0',
            }}
          >
            {endTimestamp ? getRealTime(endTime) : '--:--:--'}
          </Typography>
        </Box>
      </Box>

      <Box display="flex" alignItems="center" style={{ padding: '0' }}>
        <Box
          width="calc(100% - 6px)"
          marginLeft="4px"
          position="relative"
          className="player-slider-container"
          marginTop={marks?.length > 0 ? '20px' : 0}
        >
          {/*hoverTime !== undefined && (
            <Tooltip
              title={getRealTime(hoverTime) || ''}
              placement="top"
              open={!!hoverTime}
              arrow
              style={{
                position: 'absolute',
                left: tooltipPosition.left,
                top: tooltipPosition.top,
              }}
            >
              <span />
            </Tooltip>
          )*/}

          <Slider
            value={currentTime}
            valueLabelFormat={valueLabelFormat}
            // marks={currentDrill === null && marks.map((m) => ({...m, value: m.idx || 0}))}
            min={startTime}
            max={endTime}
            onChange={(e, v) => handleSliderChange(e, v, true)}
            onChangeCommitted={(e, v) => handleSliderChange(e, v, false)}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            disabled={!enabled || isRecording}
          />
          {currentDrill === null && (
            <SliderDrills
              marks={marks}
              min={startTime}
              max={endTime}
              // currentTimeIdx={currentTimeIdx}
              currentTime={currentTime}
              timestamps={timestamps}
            />
          )}
          {
            loadingInterval
            && (
              <SliderDataStatus
                loadingInterval={loadingInterval}
                min={startTime}
                max={endTime}
                marks={intervals}
              />
            )
          }
        </Box>
      </Box>

      <Box display="flex" alignItems="center">
        <Box width="100%" display="flex" gridGap="10px" className="player-time-controls" alignItems="center">
          <Box
            minWidth="180px"
            display="flex"
            gridGap="4px"
            alignItems="center"
            justifyContent="flex-start"
            ml="1rem"
          />

          <Box
            width="100%"
            textAlign="center"
            display="flex"
            alignItems="center"
            justifyContent="center"
            className="time-controls"
          >
            {marks.length > 0
              && (
                <BaseButton
                  isIconButton
                  noMargins
                  disabled={!enabled || isRecording || activeDrill === 0}
                  tooltipText={t('webPlayer.player.button.previousDrill', 'previous drill')}
                  onClick={() => {
                    goToPrevDrill();
                  }}
                >
                  <SkipPreviousIcon />
                </BaseButton>
              )}
            <BaseButton
              isIconButton
              noMargins
              onClick={handlePlay}
              disabled={!enabled || isRecording}
              className="player-play-button"
              tooltipText={playerStatus === 0
                ? t('webPlayer.player.button.play', 'play')
                : t('webPlayer.player.button.stop', 'stop')}
            >
              {playerStatus === 0 && <PlayArrowIcon />}
              {playerStatus === 1 && <StopIcon />}
            </BaseButton>
            {marks.length > 0
              && (
                <BaseButton
                  isIconButton
                  noMargins
                  disabled={!enabled || isRecording || activeDrill === drillTimes.length - 1}
                  tooltipText={t('webPlayer.player.button.nextDrill', 'next drill')}
                  onClick={() => {
                    goToNextDrill();
                  }}
                >
                  <SkipNextIcon />
                </BaseButton>
              )}
          </Box>

          <Box
            minWidth="180px"
            display="flex"
            gridGap="4px"
            alignItems="center"
            justifyContent="flex-end"
            className="player-optional-controls"
          >
            <HeaderPopover
              buttonId="player-speed"
              className="player-speed-popover"
              icon={playerSpeed === 0.5 ? <X05 />
                : playerSpeed === 1 ? <X1 />
                  : playerSpeed === 2.5 ? <X25 />
                    : <X5 />}
              disabled={!enabled || isRecording}
              tooltip={t('webPlayer.player.playingSpeed', 'playing speed')}
              popoverContent={[
                {
                  value: '0.5x',
                  label: '0.5x',
                  onClick: () => setPlayerSpeed(0.5),
                },
                {
                  value: '1x',
                  label: '1x',
                  onClick: () => setPlayerSpeed(1),
                },
                {
                  value: '2.5x',
                  label: '2.5x',
                  onClick: () => setPlayerSpeed(2.5),
                },
                {
                  value: '5x',
                  label: '5x',
                  onClick: () => setPlayerSpeed(5),
                },
              ]}
              topPosition
            />
            {
              me.isStaff && (
                <BaseButton isIconButton noMargins onClick={handleRecordTrack}>
                  {isRecording && <MovieFilter style={{ color: '#bf1d1d' }} />}
                  {!isRecording && <MovieFilter />}
                </BaseButton>
              )
            }
            <BaseButton
              isIconButton
              noMargins
              disabled={!enabled || isRecording}
              tooltipText={t('webPlayer.player.fullscreen', 'fullscreen')}
              onClick={handleFullscreenClick}
            >
              <FullscreenIcon />
            </BaseButton>
          </Box>
        </Box>

      </Box>
    </Box>
  );
};

export default withMe(PlayerControls);
